import { FeatureType } from '@project-gd-x/dating-site-contracts/src/gen/gdx/market/v1/package_pb';
import { computed, reactive, ref, useTemplateRef, watch } from 'vue';

import { chatRequestPromoModal } from '@/components/chat-request-promo-modal/helpers/chat-request-promo-modal/chat-request-promo-modal';
import { hideModalSpinner, showModalSpinner } from '@/components/modal/helpers/modal/modal';
import {
  POWER_LIKE_PRICE_CREDITS,
  usePowerLike,
} from '@/components/power-like/hooks/use-power-like/use-power-like';
import { useChatFooter } from '@/pages/chat/components/chat-view/components/chat-footer/hooks/use-chat-footer/use-chat-footer';
import { useStoreChatIcebreakers } from '@/pages/chat/hooks/use-store-chat-icebreakers/use-store-chat-icebreakers';
import { MyProfileStateFull } from '@/pages/my-profile/store/my-profile-state';
import { ProfileStateProfile } from '@/pages/profile/store/profile-state';
import { bq } from '@/services/bq/bq';
import {
  addNotificationHandler,
  NotificationHandler,
  removeNotificationHandler,
} from '@/services/websocket';
import { CommonStateCreditsFeatures } from '@/store/modules/common/common-state';
import { dispatch } from '@/store/store-helper';

type UseSelfArg = {
  userId: string;
};

export function useSelf(arg: UseSelfArg) {
  // Ref -----------------------------------------------------------------------
  const profile = ref<ProfileStateProfile | null>(null);
  const myProfile = ref<MyProfileStateFull | null>(null);
  const featureMap = ref<CommonStateCreditsFeatures | null>(null);
  const isPowerLikeSending = ref(false);
  const isRulesExpanded = ref(false);
  const modalContentRef = useTemplateRef<HTMLDivElement>('modalContent');
  const messageText = ref('');
  const messageToSend = ref('');
  const isSmartMatchDisabled = ref(false);

  // Hook ----------------------------------------------------------------------
  const powerLike = reactive(usePowerLike());
  const storeChatIcebreakers = useStoreChatIcebreakers(arg.userId);

  // Computed ------------------------------------------------------------------
  const featureChatRequestLeftCount = computed(() => {
    return featureMap.value?.[FeatureType.POWER_LIKE] ?? 0;
  });

  // Method --------------------------------------------------------------------
  const handleWebsocket: NotificationHandler = async (notification) => {
    if (notification.notification.case === 'sendPowerLikeSuccess') {
      const sendPowerLikeSuccess = notification.notification.value;

      if (sendPowerLikeSuccess) {
        const dialogId = sendPowerLikeSuccess.connectionId;

        const { sendTextMessage } = useChatFooter();
        sendTextMessage({
          dialogId,
          messageText: messageToSend.value,
        });

        removeNotificationHandler(handleWebsocket);
        chatRequestPromoModal.hide();
      }
    }
  };

  function sendMessageText() {
    bq.trackUserAction({
      action_name: 'chat_request_promo_send_message_text',
    });

    messageToSend.value = messageText.value;
    sendPowerLike();
  }

  function sendIceBreaker(ice: string) {
    bq.trackUserAction({
      action_name: 'chat_request_promo_send_icebreaker',
    });

    messageToSend.value = ice;
    sendPowerLike();
  }

  function watchIsRulesExpanded() {
    watch(isRulesExpanded, () => {
      if (isRulesExpanded.value) {
        setTimeout(() => {
          // modalContentRef.value?.scrollTo(0, 99999);
          // Make smooth scroll
          modalContentRef.value?.scrollTo({
            top: 99999,
            behavior: 'smooth',
          });
        }, 100);
      }
    });
  }

  function watchIsSmartMatchDisabled() {
    watch(isSmartMatchDisabled, () => {
      dispatch('actionCommonStoreIsAiGenerationAllowed', !isSmartMatchDisabled.value);
    });
  }

  function toggleIsRulesExpanded() {
    isRulesExpanded.value = !isRulesExpanded.value;
  }

  function sendPowerLike(): void {
    if (powerLike.isActive || powerLike.isAccepted || isPowerLikeSending.value) return;

    isPowerLikeSending.value = true;

    powerLike.sendPowerLikeRequest({
      credits: POWER_LIKE_PRICE_CREDITS,
    });

    showModalSpinner();

    // Fallback in case of we have an error further
    setTimeout(() => {
      hideModalSpinner();
    }, 2000);
  }

  async function fetchFeatureMap() {
    featureMap.value = await dispatch('actionCommonGetFreeCreditsFeaturesStatus');
  }

  async function fetchProfile() {
    profile.value = await dispatch('actionProfileGetProfile', arg.userId);
  }

  async function fetchMyProfile() {
    myProfile.value = await dispatch('actionMyProfileGetFull');
  }

  async function init() {
    watchIsRulesExpanded();
    watchIsSmartMatchDisabled();

    addNotificationHandler(handleWebsocket);

    await Promise.all([
      fetchProfile(),
      fetchFeatureMap(),
      fetchMyProfile(),
      storeChatIcebreakers.loadIcebreakers(),
    ]);
    powerLike.load(arg.userId);
  }

  function mount() {
    dispatch('actionCommonStoreIsAiGenerationAllowed', true);
  }

  function destroy() {
    removeNotificationHandler(handleWebsocket);
  }

  return reactive({
    profile,
    myProfile,
    isPowerLikeSending,
    isRulesExpanded,
    messageText,
    isSmartMatchDisabled,
    storeChatIcebreakers,
    featureChatRequestLeftCount,
    sendMessageText,
    sendIceBreaker,
    toggleIsRulesExpanded,
    sendPowerLike,
    init,
    mount,
    destroy,
  });
}
